import axios from '../requester';
import { API } from '../constants/api-urls';
import QueryString from 'qs';
import _ from 'lodash';
import { SENSORS_MAP } from '@linearb/linjs-common';
import {
  LBBitbucketIcon,
  LBGithubIcon,
  LBSlackIcon,
  LBGoogleIcon,
  LBGitlabIcon,
  LBAzureIcon,
  LBMsTeamsIcon,
  LBJiraIcon,
  LBAzureBoardsIcon
} from '@linearb/buzz';

export const TOKEN_VALIDATION_STATUS = {
  VALID: 'VALID',
  INVALID: 'INVALID',
  ERROR: 'ERROR',
  MISSING_SCOPES: 'MISSING_SCOPES',
  EXPIRED: 'EXPIRED',
  NOT_RELATED_TO_ORG: 'NOT_RELATED_TO_ORG'
};

export const SENSOR_AUTH_TYPES = {
  TOKEN: 'token',
  OAUTH2: 'oauth2',
  GITHUB_APP: 'github_app'
};

export const isSensorOAuth2 = sensor =>
  sensor?.profile?.auth_type === SENSOR_AUTH_TYPES.OAUTH2;

export const getSensorAuthorizations = async ({
  withProfile = false,
  withGitStreamEnabled = false,
  add_pr_stats = false,
  withoutGitHubApp
}: {
  withProfile?: boolean;
  withGitStreamEnabled?: boolean;
  add_pr_stats?: boolean;
  withoutGitHubApp?: boolean;
} = {}) =>
  axios.get(API.SENSOR_AUTHORIZATIONS_PATH, {
    params: {
      with_profile: withProfile,
      ...(withGitStreamEnabled && {
        with_gitstream_enabled: withGitStreamEnabled
      }),
      ...(add_pr_stats && { add_pr_stats }),
      ...(withoutGitHubApp === false
        ? {
            without_github_app: withoutGitHubApp
          }
        : {})
    }
  });

export const createSensorAuthorization = async (body, qs) =>
  axios.post(
    `${API.SENSOR_AUTHORIZATIONS_PATH}${
      _.isEmpty(qs) ? '' : `?${QueryString.stringify(qs)}`
    }`,
    body
  );
export const deleteSensorAuthorization = async (id, params) =>
  axios.delete(`${API.SENSOR_AUTHORIZATIONS_PATH}/${id}`, { params });
export const updateSensorAuthorization = async (id, data) =>
  axios.patch(`${API.SENSOR_AUTHORIZATIONS_PATH}/${id}`, data);

export const providerLogos = {
  google: LBGoogleIcon,
  github: LBGithubIcon,
  slack: LBSlackIcon,
  bitbucket: LBBitbucketIcon,
  bitbucket_server: LBBitbucketIcon,
  gitlab: LBGitlabIcon,
  azure_devops: LBAzureIcon,
  azure_ad_oauth2: LBMsTeamsIcon,
  JIRA: LBJiraIcon,
  azure_boards: LBAzureBoardsIcon
};

export const getSensorScopesValidation = async sensorId => {
  return axios.get(`${API.SENSOR_AUTHORIZATIONS_PATH}/${sensorId}/validate`);
};

export const getIsSensorHasData = async sensorId => {
  return axios.get(`${API.SENSOR_AUTHORIZATIONS_PATH}/${sensorId}/has-data`);
};

export const getGitHubAppInstallUrl = async () => {
  return axios.post(API.GITHUB_APP_INSTALL_PATH);
};

/**
 * Check if GitHub App installation already exists
 */
export const checkExistingGitHubAppInstallation = async installation_id => {
  try {
    // Always include GitHub App sensor authorizations when specifically checking for them
    const response = await getSensorAuthorizations({
      withProfile: true,
      withoutGitHubApp: false
    });

    const existingSensorAuth = response.data.find(
      sa => sa.profile?.installation_id === installation_id
    );

    return {
      exists: !!existingSensorAuth,
      sensor_auth: existingSensorAuth
    };
  } catch (error) {
    return { exists: false, sensor_auth: null };
  }
};

/**
 * Create GitHub App sensor authorization
 */
export const createGitHubAppSensorAuthorization = async installation_id => {
  const sensorAuthData = {
    sensor_id: SENSORS_MAP.GITHUB,
    name: 'LinearB Github App',
    authorization_level: 'organization',
    state: 'ok',
    token: {}, // Empty token - will be populated from env vars
    profile: {
      auth_type: SENSOR_AUTH_TYPES.GITHUB_APP,
      installation_id
    }
  };

  try {
    const response = await createSensorAuthorization(sensorAuthData, undefined);
    return response.data;
  } catch (error) {
    throw error;
  }
};
